import { GlobeIcon } from '@radix-ui/react-icons';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { ErrorBoundary } from 'components/common/error-boundary';
import { SidebarNavigationTrigger } from 'components/main/sidebar/nav-trigger';
import { t } from 'i18next';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { mean } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { MainService } from 'services/main.service';

const INTERVAL_SECONDS = 30;

// smaller value => more aggressive warnings
const MAX_MS_BEFORE_WARN = 1_000;

export const ConnectionWarning = () => {
  const [dialog, setDialog] = useState<number>();

  // in ms
  const [pings, setPings] = useState<number[]>([]);

  const show = useMemo(() => {
    if (pings.length === 0) {
      return false;
    }

    const meanMs = mean(pings);
    return meanMs > MAX_MS_BEFORE_WARN;
  }, [pings]);

  useEffect(() => {
    const interval = setInterval(async () => {
      // even if banner is showing, we will still check again in case the connection improves and we can hide
      const start = performance.now();

      await MainService.getInstance().ping().catch(console.error);

      const ping = performance.now() - start;
      setPings((prev) => [...prev, ping].slice(-6));
    }, INTERVAL_SECONDS * 1_000);

    return () => clearInterval(interval);
  }, []);

  if (!show) {
    return null;
  }

  return (
    <ErrorBoundary identifier="ConnectionWarning">
      <SidebarNavigationTrigger
        label="common.slow-connection-detected"
        icon={<GlobeIcon />}
        color={RADIX.COLOR.WARNING}
        onClick={() => setDialog(Date.now())}
        small
      />

      {dialog && (
        <CommonConfirmationDialog
          key={dialog}
          identifier="ConnectionWarningDescription"
          title="common.slow-connection-detected"
          content={<ReactMarkdown children={t('common.slow-connection-msg')} />}
          cancel={{
            label: 'common.dismiss',
            color: RADIX.COLOR.ACCENT,
          }}
          action={{
            invisible: true,
          }}
        />
      )}
    </ErrorBoundary>
  );
};
